import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type BannerResponse, parseBannerLink, pickLocalized } from '@aps/contracts';
import { Button, Skeleton, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages, type Messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { BannerEditor } from './BannerEditor.js';
import { bannerState, type BannerState } from './banner-draft.js';
import { QueueError } from './WorkQueueScreen.js';

const STATE_BADGE: Record<BannerState, string> = {
  live: 'active',
  hidden: 'archived',
  scheduled: 'draft',
  expired: 'rejected',
};

function stateLabel(state: BannerState, t: Messages): string {
  switch (state) {
    case 'live':
      return t.refBannerStateLive;
    case 'hidden':
      return t.refBannerStateHidden;
    case 'scheduled':
      return t.refBannerStateScheduled;
    case 'expired':
      return t.refBannerStateExpired;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/** Havola bir qatorda: "Tovar sahifasiga · kyb-339788". */
function linkSummary(linkUrl: string | null, t: Messages): string {
  const link = parseBannerLink(linkUrl);
  if (link === null) return t.refBannerNoLink;
  switch (link.type) {
    case 'product':
      return `${t.refBannerLinkProduct} · ${link.slug}`;
    case 'category':
      return t.refBannerLinkCategory;
    case 'catalog':
      return t.refBannerLinkCatalog;
    case 'search':
      return `${t.refBannerLinkSearch} · ${link.query}`;
    default: {
      const exhaustive: never = link;
      return exhaustive;
    }
  }
}

function dateText(value: string, locale: 'UZ' | 'RU'): string {
  return new Date(value).toLocaleDateString(locale === 'RU' ? 'ru' : 'uz');
}

/**
 * Bannerlar: kartalar ro'yxati. Har kartada rasm, holat ("mijozga ko'rinmoqda / yashirilgan /
 * muddati tugagan") va amallar. Yaratish/tahrirlash — alohida oynada (`BannerEditor`),
 * o'chirish — tasdiqlash bilan, xato esa amal yonida chiqadi.
 */
export function BannersPanel(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  // `'new'` — yangi banner, banner — tahrirlash, `null` — oyna yopiq.
  const [editor, setEditor] = useState<BannerResponse | 'new' | null>(null);
  const [deleting, setDeleting] = useState<BannerResponse | null>(null);
  const [rowError, setRowError] = useState<{ id: string; text: string } | null>(null);

  const banners = useQuery({ queryKey: ['banners'], queryFn: () => api.banners() });
  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['banners'] });
  };
  const errorText = (cause: unknown): string =>
    cause instanceof ApiError ? cause.text(locale) : t.errorTitle;

  const toggle = useMutation({
    mutationFn: (row: BannerResponse) => api.updateBanner(row.id, { isActive: !row.isActive }),
    onMutate: () => {
      setRowError(null);
    },
    onSuccess: () => {
      refresh();
      toast.show(t.toastSaved, 'success');
    },
    onError: (cause, row) => {
      setRowError({ id: row.id, text: errorText(cause) });
    },
  });

  if (banners.isPending) return <Skeleton height="240px" label={t.loading} />;
  if (banners.isError) {
    return (
      <QueueError
        message={errorText(banners.error)}
        retryLabel={t.retry}
        onRetry={() => void banners.refetch()}
      />
    );
  }

  const now = new Date();
  const rows = banners.data;

  return (
    <section className="adm-panel">
      <div className="adm-banners-head">
        <div>
          <h2 className="adm-banners-head__title">{t.refBanners}</h2>
          <p className="aps-muted">{t.refBannersLead}</p>
        </div>
        <Button
          onClick={() => {
            setEditor('new');
          }}
        >
          + {t.refAddBanner}
        </Button>
      </div>
      <p className="aps-muted adm-banners-note">{t.refBannersCacheNote}</p>

      {rows.length === 0 ? (
        <div className="adm-banners-empty">
          <strong>{t.refBannersEmpty}</strong>
          <p className="aps-muted">{t.refBannersEmptyHint}</p>
          <Button
            onClick={() => {
              setEditor('new');
            }}
          >
            + {t.refAddBanner}
          </Button>
        </div>
      ) : (
        <ul className="adm-banners">
          {rows.map((row) => {
            const state = bannerState(row, now);
            const title = pickLocalized(row.titleUz, row.titleRu, locale);
            const range = [
              row.startsAt === null ? null : `${t.refBannerFrom} ${dateText(row.startsAt, locale)}`,
              row.endsAt === null ? null : `${t.refUntil} ${dateText(row.endsAt, locale)}`,
            ].filter((part): part is string => part !== null);
            return (
              <li className="adm-banner-card" data-state={state} key={row.id}>
                <div className="adm-banner-card__media" data-cat={row.colorKey ?? undefined}>
                  {row.imageUrl === null ? (
                    <span className="adm-banner-card__noimage">{t.refBannerNoImage}</span>
                  ) : (
                    <img src={row.imageUrl} alt="" width={1200} height={620} loading="lazy" />
                  )}
                </div>
                <div className="adm-banner-card__body">
                  <div className="adm-banner-card__top">
                    <strong className="adm-banner-card__title">{title}</strong>
                    <span className={`adm-badge adm-badge--${STATE_BADGE[state]}`}>
                      {stateLabel(state, t)}
                    </span>
                  </div>
                  {row.subtitleUz !== null && (
                    <p className="aps-muted">
                      {pickLocalized(row.subtitleUz, row.subtitleRu, locale)}
                    </p>
                  )}
                  <p className="aps-muted adm-banner-card__meta">
                    {[linkSummary(row.linkUrl, t), ...range].join(' · ')}
                  </p>
                </div>
                <div className="adm-banner-card__actions">
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label={`${t.refEditBanner}: ${title}`}
                    onClick={() => {
                      setEditor(row);
                    }}
                  >
                    {t.refBannerEditShort}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={toggle.isPending}
                    aria-label={`${row.isActive ? t.refHide : t.refShow}: ${title}`}
                    onClick={() => {
                      toggle.mutate(row);
                    }}
                  >
                    {row.isActive ? t.refHide : t.refShow}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    aria-label={`${t.refDelete}: ${title}`}
                    onClick={() => {
                      setDeleting(row);
                    }}
                  >
                    {t.refDelete}
                  </Button>
                </div>
                {rowError?.id === row.id && (
                  <p className="aps-error adm-banner-card__error" role="alert">
                    {rowError.text}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editor !== null && (
        <BannerEditor
          key={editor === 'new' ? 'new' : editor.id}
          banner={editor === 'new' ? null : editor}
          onClose={() => {
            setEditor(null);
          }}
        />
      )}
      {deleting !== null && (
        <DeleteBannerDialog
          banner={deleting}
          onClose={() => {
            setDeleting(null);
          }}
        />
      )}
    </section>
  );
}

/**
 * O'chirishni tasdiqlash. Xato oynaning O'ZIDA chiqadi (sahifa pastida emas) va oyna yopilmaydi:
 * foydalanuvchi nima bo'lganini ko'radi. "Yashirish" — qaytariladigan xavfsiz muqobil.
 */
function DeleteBannerDialog({
  banner,
  onClose,
}: {
  banner: BannerResponse;
  onClose: () => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const done = (message: string): void => {
    void client.invalidateQueries({ queryKey: ['banners'] });
    toast.show(message, 'success');
    onClose();
  };
  const fail = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };
  const remove = useMutation({
    mutationFn: () => api.deleteBanner(banner.id),
    onSuccess: () => {
      done(t.toastDeleted);
    },
    onError: fail,
  });
  const hide = useMutation({
    mutationFn: () => api.updateBanner(banner.id, { isActive: false }),
    onSuccess: () => {
      done(t.toastSaved);
    },
    onError: fail,
  });
  const busy = remove.isPending || hide.isPending;

  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = busy ? () => undefined : onClose;
  });
  useEffect(() => {
    const previous = document.activeElement;
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);

  return (
    <div className="adm-modal">
      <div className="adm-modal__backdrop" aria-hidden="true" />
      <div
        ref={panelRef}
        className="adm-modal__panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 id={titleId} className="adm-banner-editor__title">
          {t.refBannerDeleteTitle}
        </h2>
        <p>
          <strong>{pickLocalized(banner.titleUz, banner.titleRu, locale)}</strong>
        </p>
        <p className="aps-muted">{t.refBannerDeleteBody}</p>
        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}
        <div className="adm-banner-editor__actions">
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t.cancel}
          </Button>
          {banner.isActive && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setError(null);
                hide.mutate();
              }}
            >
              {t.refHide}
            </Button>
          )}
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => {
              setError(null);
              remove.mutate();
            }}
          >
            {t.refDelete}
          </Button>
        </div>
      </div>
    </div>
  );
}
